import env from "config/env";
import { useEffect, useState } from "react";

export const ZENDESK_STORAGE_KEY = "zauth";
export const ZENDESK_REFRESH_STORAGE_KEY = "zauth_refresh";
export const ZENDESK_EXPIRY_STORAGE_KEY = "zauth_expiry";
export const ZENDESK_CLIENT_ID = "bonver_integration_for_zendesk";

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export interface ZendeskTokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

export const saveZendeskTokens = (data: ZendeskTokenResponse) => {
  if (!data.access_token) return false;
  localStorage.setItem(ZENDESK_STORAGE_KEY, data.access_token);
  if (data.refresh_token) {
    localStorage.setItem(ZENDESK_REFRESH_STORAGE_KEY, data.refresh_token);
  }
  if (data.expires_in) {
    localStorage.setItem(
      ZENDESK_EXPIRY_STORAGE_KEY,
      (Date.now() + data.expires_in * 1000).toString(),
    );
  } else {
    localStorage.removeItem(ZENDESK_EXPIRY_STORAGE_KEY);
  }
  return true;
};

export const clearZendeskTokens = () => {
  localStorage.removeItem(ZENDESK_STORAGE_KEY);
  localStorage.removeItem(ZENDESK_REFRESH_STORAGE_KEY);
  localStorage.removeItem(ZENDESK_EXPIRY_STORAGE_KEY);
};

let refreshPromise: Promise<string | null> | null = null;

const requestRefresh = async (refreshToken: string) => {
  try {
    const response = await fetch("/api/zendesk-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: ZENDESK_CLIENT_ID,
      }),
    });
    if (response.ok) {
      const data: ZendeskTokenResponse = await response.json();
      if (saveZendeskTokens(data)) return data.access_token!;
    } else {
      console.error(
        "Zendesk token refresh failed:",
        response.status,
        await response.text(),
      );
    }
  } catch (err) {
    console.error("Error refreshing Zendesk token:", err);
  }
  return null;
};

const runRefresh = async (seenRefreshToken: string | null) => {
  const refreshToken = localStorage.getItem(ZENDESK_REFRESH_STORAGE_KEY);
  const accessToken = localStorage.getItem(ZENDESK_STORAGE_KEY);
  if (refreshToken && accessToken && refreshToken !== seenRefreshToken) {
    return accessToken;
  }
  if (!refreshToken) {
    clearZendeskTokens();
    return null;
  }

  const token = await requestRefresh(refreshToken);
  if (token) return token;

  if (localStorage.getItem(ZENDESK_REFRESH_STORAGE_KEY) !== refreshToken) {
    return localStorage.getItem(ZENDESK_STORAGE_KEY);
  }
  clearZendeskTokens();
  return null;
};

export const refreshZendeskToken = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;

  const seenRefreshToken = localStorage.getItem(ZENDESK_REFRESH_STORAGE_KEY);
  if (!seenRefreshToken) {
    clearZendeskTokens();
    return Promise.resolve(null);
  }

  refreshPromise = (
    navigator.locks
      ? navigator.locks.request("zendesk-token-refresh", () =>
          runRefresh(seenRefreshToken),
        )
      : runRefresh(seenRefreshToken)
  ).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export const getZendeskTokenWithRefresh = async (): Promise<string | null> => {
  const token = localStorage.getItem(ZENDESK_STORAGE_KEY);
  const expiry = localStorage.getItem(ZENDESK_EXPIRY_STORAGE_KEY);
  if (token && (!expiry || Date.now() < Number(expiry) - REFRESH_BUFFER_MS)) {
    return token;
  }
  return refreshZendeskToken();
};

// PKCE helpers
function base64UrlEncode(str: string) {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest("SHA-256", data);
}

async function pkceChallengeFromVerifier(verifier: string) {
  const hashed = await sha256(verifier);
  const bytes = new Uint8Array(hashed);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return base64UrlEncode(binary);
}

function randomString(length: number) {
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  let result = "";
  const values = window.crypto.getRandomValues(new Uint8Array(length));
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length];
  }
  return result;
}

let reauthStarted = false;

export const reauthenticateZendesk = () => {
  if (reauthStarted) return;
  reauthStarted = true;
  clearZendeskTokens();
  startAuthFlow();
};

const startAuthFlow = async () => {
  const codeVerifier = randomString(64);
  const codeChallenge = await pkceChallengeFromVerifier(codeVerifier);
  localStorage.setItem("zendesk_pkce_verifier", codeVerifier);
  const endpoint = `${env.zendeskUrl}/oauth/authorizations/new`;
  const url_params = `?response_type=code&redirect_uri=${
    env.redirectUri
  }&client_id=${ZENDESK_CLIENT_ID}&scope=${encodeURIComponent(
    "read write",
  )}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  window.location.href = endpoint + url_params;
};

export const useZendesk = () => {
  const [token, setToken] = useState<string | null>();
  useEffect(() => {
    let cancelled = false;
    getZendeskTokenWithRefresh().then((currentToken) => {
      if (cancelled) return;
      const promptStatus = localStorage.getItem("zendeskAuthPromptShown");
      if (currentToken) {
        setToken(currentToken);
      } else if (promptStatus !== "1") {
        localStorage.setItem("zendeskAuthPromptShown", "1");
        const confirmed = window.confirm(
          "You need to authenticate with Zendesk to continue. Do you want to proceed?",
        );
        if (confirmed) {
          startAuthFlow();
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return token;
};
